/**
 * The answer state machine (moved verbatim from StepView.vue):
 * correct = sound → save → speak the answer → present the next try;
 * wrong = sound → [feedback dialog] → save per handling_of_mistakes;
 * timeout (Speed Round) = a wrong try marked isTimeout.
 * Nothing runs after the view is gone (BUG-24, `isActive`).
 */
import { nextTick, ref, toValue, type MaybeRefOrGetter, type Ref } from 'vue';
import { t } from 'boot/i18n';
import { mistakeTry, type MistakeTry } from '../utilities/mistakeTry';
import type { UseDrillAudioReturn } from './useDrillAudio';
import type { UseDrillFeedbackDialogsReturn } from './useDrillFeedbackDialogs';

/** One recorded try (useStep's ProgressData). */
export type AnswerFlowProgressData = MistakeTry;

/** The drill settings the flow reads (a DrillSettings satisfies it). */
export interface AnswerFlowSettings {
  feedback_for_mistakes?: string;
  handling_of_mistakes?: string;
}

export type AnswerFlowAudio = Pick<
  UseDrillAudioReturn,
  'playAnswer' | 'presentQuestion' | 'notifyRight' | 'notifyWrong'
> &
  Partial<Pick<UseDrillAudioReturn, 'resetQuestionAudio'>>;

export interface AnswerFlowTimer {
  timeMiliseconds: Readonly<Ref<number>>;
  start: () => void;
  stop: () => void;
  reset: () => void;
}

export interface UseAnswerFlowOptions {
  checkAnswer: (answer: unknown) => boolean | undefined;
  updateProgress: (data: AnswerFlowProgressData) => unknown;
  answerText: (answer: unknown, questionText?: string) => unknown;
  getFeedbackSubtitle: (questionText: string) => string;
  getFeedbackAnswer: (answer: string) => string;
  getCorrectFeedbackAnswer: () => string;
  /** The answer the HELP dialog shows for the current question. */
  helpAnswer: () => string | string[] | null | undefined;
  drillSettings: MaybeRefOrGetter<AnswerFlowSettings | null | undefined>;
  resetStepData: () => Promise<unknown>;
  audio: AnswerFlowAudio;
  dialogs: UseDrillFeedbackDialogsReturn;
  timer: AnswerFlowTimer;
  isActive: () => boolean;
  questionText: MaybeRefOrGetter<string>;
}

export function useAnswerFlow(opts: UseAnswerFlowOptions) {
  const { audio, dialogs, timer, isActive } = opts;
  const isCheckingAnswer = ref(false);

  async function resetStep() {
    isCheckingAnswer.value = false;
    timer.stop();
    timer.reset();
    audio.resetQuestionAudio?.();
    await opts.resetStepData();
    // Ready for the first question again: time it from 0.
    if (isActive()) timer.start();
  }

  function getHelp() {
    const correctAnswer = opts.helpAnswer();

    // Stop timer
    timer.stop();

    dialogs.openHelp(correctAnswer);
    // D-8: the help try is recorded when the dialog opens.
    handleMistakes(false, t('__ASK_FOR_HELP'), true);
  }

  async function checkSelectedAnswer(answer: unknown) {
    timer.stop();
    const result: boolean | undefined = opts.checkAnswer(answer);
    if (result === undefined) return;

    // The playQuestion function set isCheckingAnswer to false
    isCheckingAnswer.value = true;

    try {
      if (result) {
        // Read before the save: the drill moves on to the next question.
        const spokenAnswer = opts.answerText(
          answer,
          toValue(opts.questionText),
        );
        // Sound + save first, so leaving during the speech loses nothing (BUG-7).
        audio.notifyRight();
        opts.updateProgress({
          result,
          answer: answer as string,
          duration: timer.timeMiliseconds.value,
        });
        await nextTick(); // the progress PUT goes out before the speech
        if (!isActive()) return;
        await audio.playAnswer(spokenAnswer as string);
        if (!isActive()) return;
        await nextTick();
        await audio.presentQuestion();
      } else {
        audio.notifyWrong();
        switch (toValue(opts.drillSettings)?.feedback_for_mistakes) {
          case 'Show Error Message':
            dialogs.openError();
            handleMistakes(result, answer as string);
            break;
          case 'Show Answer Comparison':
            dialogs.openComparison({
              subTitle: opts.getFeedbackSubtitle(toValue(opts.questionText)),
              yourAnswer: opts.getFeedbackAnswer(answer as string),
              correctAnswer: opts.getCorrectFeedbackAnswer(),
            });
            handleMistakes(result, answer as string);
            break;
          default:
            handleMistakes(result, answer as string);
            await nextTick();
            await audio.presentQuestion();
            break;
        }
      }
    } catch (error) {
      console.error(error);
    }
    isCheckingAnswer.value = false;
  }

  function handleMistakes(
    result: boolean,
    answer: string,
    isHelp?: boolean,
    isTimeout?: boolean,
  ) {
    const progressData = mistakeTry(
      result,
      answer,
      timer.timeMiliseconds.value,
      isHelp,
      toValue(opts.drillSettings)?.handling_of_mistakes,
    );
    if (progressData && isTimeout) progressData.isTimeout = true;
    if (progressData) opts.updateProgress(progressData);
  }

  /** The time limit ran out: a wrong try, with the same feedback as one. */
  async function handleTimeout() {
    if (isCheckingAnswer.value || !isActive()) return;
    timer.stop();
    isCheckingAnswer.value = true;
    // Like help, no answer is stored: results lists show the text (isTimeout).
    const timeUp = t('__TIME_IS_UP');

    try {
      audio.notifyWrong();
      switch (toValue(opts.drillSettings)?.feedback_for_mistakes) {
        case 'Show Error Message':
          dialogs.openError();
          handleMistakes(false, '', false, true);
          break;
        case 'Show Answer Comparison':
          dialogs.openComparison({
            subTitle: opts.getFeedbackSubtitle(toValue(opts.questionText)),
            yourAnswer: timeUp,
            correctAnswer: opts.getCorrectFeedbackAnswer(),
          });
          handleMistakes(false, '', false, true);
          break;
        default:
          handleMistakes(false, '', false, true);
          await nextTick();
          await audio.presentQuestion();
          break;
      }
    } catch (error) {
      console.error(error);
    }
    isCheckingAnswer.value = false;
  }

  return {
    isCheckingAnswer,
    checkSelectedAnswer,
    handleMistakes,
    getHelp,
    handleTimeout,
    resetStep,
  };
}

export type UseAnswerFlowReturn = ReturnType<typeof useAnswerFlow>;
